import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ErrorStateMatcher } from "@angular/material/core";
import { ActivatedRoute, Router } from "@angular/router";
import { INVALID_PASSWORDS } from "app/shared/constants/invalid-passwords.constant";
import { PasswordConfirmationErrorMatcher } from "app/shared/error-matchers/password-confirmation.matcher";
import { switchMap } from "rxjs/operators";
import { AccountCasesService } from "../../shared/services/account-cases.service";
import { AccountService } from "../../shared/services/account.service";
import { ErrorReportingService } from "../../shared/services/error-reporting.service";
import { AccountCases } from "../../shared/types/account-case.type";
import { Account } from "../../shared/types/account.type";
import { Case } from "../../shared/types/case.type";
import { Credential } from "../../shared/types/credential.type";
import { Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NgForm } from "@angular/forms";
import { PasswordComplexity } from "@cariloop/ui-components";

/**
 * @Component request password
 */
@Component({
  selector: "request-password",
  styleUrls: ["./request-password.scss"],
  templateUrl: "./request-password.html",
  standalone: false,
})
export class RequestPasswordComponent implements OnInit, AfterViewChecked {
  @ViewChild("successHeader")
  successHeader: ElementRef<HTMLHeadingElement>;
  public credentials: Credential = new Credential();
  public error: string;
  public isLoading = false;
  public isPasswordValid = true;
  public accountToUpdate: Account = new Account();
  public _case: Case;
  public loginCode: string;
  public errorMatcher: ErrorStateMatcher;
  public termsAccepted = false;
  public passwordComplexity: PasswordComplexity;
  private isCode = false;
  public shouldShowSuccessMessage = false;
  private hasShownInitialSuccessFocus: boolean;

  constructor(
    private accountService: AccountService,
    private accountCasesService: AccountCasesService,
    private router: Router,
    private errorReportingService: ErrorReportingService,
    private route: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService,
    private destroyRef: DestroyRef,
  ) {
    this.errorMatcher = new PasswordConfirmationErrorMatcher();
  }

  public ngOnInit(): void {
    this.translateService
      .stream("REQUEST_PASSWORD.PAGE_TITLE")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pageTitle) => {
        this.titleService.setTitle(pageTitle + " | Cariloop");
      });

    const account = this.route.snapshot.data["account"];
    const loginCode = this.route.snapshot.params["id"];
    const isCode = this.route.snapshot.data["isCode"];

    if (!account) {
      this.router.navigate(["login"]);
      return;
    }

    this.accountToUpdate = account;
    this.loginCode = loginCode;
    this.isCode = isCode;
    this.setPasswordComplexity();
  }

  public ngAfterViewChecked(): void {
    if (!this.hasShownInitialSuccessFocus && this.successHeader) {
      this.successHeader.nativeElement.focus();
      this.hasShownInitialSuccessFocus = true;
    }
  }

  public onSubmit(form: NgForm): void {
    if (form.invalid) {
      form.controls.password.markAsTouched();
      form.controls.terms.markAsTouched();
      return;
    }
    this.error = null;
    this.isLoading = true;
    this.changePassword();
  }

  public validatePassword(): boolean {
    const { password } = this.credentials;
    return (this.isPasswordValid = password
      ? !INVALID_PASSWORDS[password]
      : true);
  }

  public changePassword(): void {
    this.accountService
      .change(this.credentials, this.loginCode, new Date(), this.isCode)
      .subscribe(
        () => {
          this.shouldShowSuccessMessage = true;
        },
        (error) => this.errorHandler(error),
      );
  }

  public getAccountCase() {
    this.accountCasesService
      .getAccountCases(this.accountToUpdate.id, {
        where: {
          accountId: this.accountToUpdate.id,
        },
        order: "role",
      })
      .subscribe(
        (accountCases: AccountCases[]) => {
          if (
            accountCases &&
            Array.isArray(accountCases) &&
            accountCases.length > 0
          ) {
            accountCases = accountCases.filter((ac) => !ac.deletedAt);
            window.location.href = this.accountService.getHomeUrl();
          } else {
            const error = new Error(
              `No cases found for: ${this.accountToUpdate.id}`,
            );
            this.errorHandler(error);
          }
        },
        (err: Error) => this.errorHandler(err),
      );
  }

  private setPasswordComplexity() {
    if (this.accountToUpdate.provisioningProfile?.passwordComplexity?.value) {
      this.passwordComplexity = {
        ...this.accountToUpdate.provisioningProfile?.passwordComplexity?.value,
        containsNameOrEmail: {
          firstName: this.accountToUpdate.firstName,
          lastName: this.accountToUpdate.lastName,
          email: this.accountToUpdate.email,
        },
      };
    }
  }

  private errorHandler(err): void {
    this.isLoading = false;
    this.error = err;
    if (err.status === 404) {
      const userPath = this.route.url[0].path;
      const flowType = userPath === "confirm" ? "user" : "coach"; // "confirm" is the only path that needs to redirect to the user flow
      this.router.navigate(["expired-link"], {
        state: { flow: flowType, originPath: userPath },
      });
    } else if (err.status !== 400 && err.status !== 451) {
      this.errorReportingService.send(err);
    }
  }
}
